import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useTitle from '../../helpers/useTitle';
import { Table, Column } from '@appkit4/react-components/table';
import {
    Button,
    Panel,
    Loading,
    Tooltip,
    Notice,
    Pagination,
} from '@appkit4/react-components';
import ConfirmDeleteModal from '../../components/pages/history/modals/confirmDelete/ConfirmDeleteModal';
import { useGetV1SelfDeclarationPaginatedHistory } from '../../api/generated/endpoint';
import {
    GetV1SelfDeclarationPaginatedHistoryParams,
    OrderByRequest,
    PlannedSubmission,
} from '../../api/generated/models';
import useLocale from '../../locale/useLocale';
import useGenericErrorToast from 'helpers/useGenericErrorToast';
import DateCellRenderer from 'components/pages/history/DateCellRenderer';
import { AppRoutes } from 'Constants';
import usePaginationControl from 'helpers/usePaginationControl';
import renderReasonForAbsence from './renderReasonForAbsence';

export type HistoryRow = {
    submissionId: string | null;
    declarationType: string;
    reasonForAbsence: string | string[];
    dateRanges: { startDate: string; endDate: string }[] | string;
    totalDaysAbsent: number | string;
    createdDate: string;
};

const unforeseenCircumstances = 'Unforeseen Circumstances';
const plannedCircumstances = 'Planned Circumstances';
export const DataCorrectionAttendedoffice =
    'Data Correction - Attended PwC Office';

const History = () => {
    const { l } = useLocale();
    const navigate = useNavigate();
    const showGenericErrorToast = useGenericErrorToast();

    useTitle(l('_historyPageTitle'));

    const [historyParams, setHistoryParams] =
        useState<GetV1SelfDeclarationPaginatedHistoryParams>({
            pageNumber: 1,
            pageSize: 5,
            orderBy: OrderByRequest.DateDescending,
        });

    const {
        data: paginatedHistoryData,
        isPending: paginatedHistoryDataIsPending,
        error: paginatedHistoryError,
    } = useGetV1SelfDeclarationPaginatedHistory(historyParams);

    usePaginationControl(
        { totalCount: paginatedHistoryData?.totalCount },
        historyParams.pageSize || 5,
        setHistoryParams,
        historyParams.pageNumber || 1
    );

    const historyTableData = useMemo(() => {
        if (!paginatedHistoryData || !paginatedHistoryData.items) return [];
        const orderedHistoryData = paginatedHistoryData.items
            .map((submission) => {
                if (
                    submission.declarationType === plannedCircumstances &&
                    submission.dateRanges
                ) {
                    return {
                        submissionId: submission.submissionId,
                        declarationType: submission.declarationType,
                        reasonForAbsence: submission.reasonForAbsence,
                        dateRanges: submission.dateRanges,
                        totalDaysAbsent: submission.totalDaysAbsent,
                        createdDate: submission.createdDate,
                    };
                }

                if (
                    submission.declarationType === unforeseenCircumstances &&
                    submission.details
                ) {
                    return {
                        submissionId: submission.submissionId,
                        declarationType: submission.declarationType,
                        reasonForAbsence: submission.details.map(
                            (detail) => detail.reasonForAbsence
                        ),
                        dateRanges: submission.details.map((detail) => ({
                            startDate: detail.startDate,
                            endDate: detail.startDate,
                        })),
                        totalDaysAbsent: submission.totalDaysAbsent,
                        createdDate: submission.createdDate,
                    };
                }
                return null;
            })
            .filter((submission) => submission !== null);

        return orderedHistoryData;
    }, [paginatedHistoryData]);

    const [deleteModalVisible, setDeleteModalVisible] = useState(false);

    const [submissionToDelete, setSubmissionToDelete] =
        useState<PlannedSubmission | null>(null);

    const handleDeleteClick = (row: PlannedSubmission) => {
        setSubmissionToDelete(row);
        setDeleteModalVisible(true);
    };

    const shouldShowEditButton = (createdDate: string) => {
        const today = new Date();
        const threeMonthsAgo = new Date(
            today.getFullYear(),
            today.getMonth() - 3,
            1
        );

        return new Date(createdDate).getTime() > threeMonthsAgo.getTime();
    };

    const handleEditClick = (row: any) => {
        const isConsecutiveDates =
            Array.isArray(row.dateRanges) &&
            row.dateRanges.every(
                (
                    range: { startDate: string; endDate: string },
                    index: number,
                    arr: { startDate: string; endDate: string }[]
                ) => {
                    if (index === 0) return true;
                    const prevEndDate = new Date(arr[index - 1].endDate);
                    const currentStartDate = new Date(range.startDate);
                    return (
                        currentStartDate.getTime() - prevEndDate.getTime() ===
                        24 * 60 * 60 * 1000
                    );
                }
            );

        const isSameReason =
            Array.isArray(row.reasonForAbsence) &&
            row.reasonForAbsence.every(
                (reason: any) => reason === row.reasonForAbsence[0]
            );

        const lengthGreaterThanOne =
            Array.isArray(row.reasonForAbsence) &&
            row.reasonForAbsence.length > 1;

        navigate(
            AppRoutes.EditDeclaration.replace(
                ':submissionId',
                String(row?.submissionId || '')
            ).replace(
                ':declarationType',
                String(row?.declarationType || '')
                    .replace(/\s+/g, '-')
                    .toLowerCase()
            ),
            {
                state:
                    isConsecutiveDates && isSameReason && lengthGreaterThanOne
                        ? {
                              consecutiveUnforeseenAndSameReason: true,
                          }
                        : undefined,
            }
        );
    };

    const renderActionsCell = (row: any) => {
        const disabledOption =
            row.declarationType === unforeseenCircumstances &&
            row.reasonForAbsence[0] === DataCorrectionAttendedoffice;

        return historyTableData?.length ? (
            <div className="d-flex">
                {shouldShowEditButton(row.createdDate) ? (
                    <Tooltip
                        trigger="hover"
                        position="top-left"
                        distance={4}
                        id="tooltipDesc"
                        appendAfterTarget={true}
                        content={l('_edit')}
                    >
                        <Button
                            add
                            kind="text"
                            icon="icon-edit-outline"
                            ariaLabel={l('_edit')}
                            onClick={() => handleEditClick(row)}
                            disabled={disabledOption}
                        />
                    </Tooltip>
                ) : null}

                <Tooltip
                    trigger="hover"
                    position="top-left"
                    distance={4}
                    id="tooltipDesc"
                    appendAfterTarget={true}
                    content={l('_delete')}
                >
                    <Button
                        add
                        kind="text"
                        onClick={() => handleDeleteClick(row)}
                        icon="icon-delete-outline"
                        ariaLabel={l('_delete')}
                        disabled={disabledOption}
                    />
                </Tooltip>
            </div>
        ) : null;
    };

    useEffect(() => {
        if (paginatedHistoryError) {
            showGenericErrorToast(paginatedHistoryError);
        }
    }, [paginatedHistoryError, showGenericErrorToast]);

    const noDataRow = {
        declarationType: '',
        reasonForAbsence: l('_historyPageTableNoSubmissions'),
        dateRanges: '',
        totalDaysAbsent: '',
        submissionId: null,
    };

    const tableData = historyTableData?.length
        ? historyTableData ?? []
        : [noDataRow];

    return (
        <Panel className={'mt-4'} style={{ minWidth: '80%' }}>
            <div className="d-flex justify-content-between pt-2">
                <div>
                    <h1>{l('_historyPageSubheading')}</h1>
                </div>
                <Notice
                    title=""
                    message={l('_submissionDelay')}
                    status="icon-information-outline"
                    closeable={false}
                    className="mx-0 ms-auto"
                    style={{
                        minWidth: '300px',
                        maxWidth: '80%',
                        width: '80%',
                    }}
                ></Notice>
            </div>
            <ConfirmDeleteModal
                visible={deleteModalVisible}
                setVisible={setDeleteModalVisible}
                submissionToDelete={submissionToDelete}
                historyParams={historyParams}
            />
            {paginatedHistoryDataIsPending ? (
                <Loading
                    loadingType="circular"
                    indeterminate
                    compact={false}
                    className="mt-2"
                />
            ) : (
                <>
                    <Table
                        originalData={tableData}
                        hasTitle
                        striped
                        style={{ tableLayout: 'fixed', width: '100%' }}
                    >
                        <Column
                            field="declarationType"
                            style={{ width: '25%' }}
                        >
                            {l('_historyTableCircumstanceType')}
                        </Column>
                        <Column
                            field="reasonForAbsence"
                            style={{ width: '45%' }}
                            renderCell={renderReasonForAbsence}
                        >
                            {l('_historyTableCircumstanceDetail')}
                        </Column>
                        <Column
                            field="dateRanges"
                            renderCell={DateCellRenderer}
                            style={{ width: '15%' }}
                        >
                            {l('_historyTableDates')}
                        </Column>
                        <Column
                            field="totalDaysAbsent"
                            style={{ width: '10%' }}
                        >
                            {l('_historyTableDays')}
                        </Column>
                        <Column
                            field="submissionId"
                            renderCell={renderActionsCell}
                            style={{ width: '10%' }}
                        >
                            {l('_actions')}
                        </Column>
                    </Table>
                    {!paginatedHistoryDataIsPending &&
                        paginatedHistoryData?.items &&
                        paginatedHistoryData.items.length > 0 && (
                            <Pagination
                                data-testid="pagination"
                                total={
                                    historyParams.pageSize
                                        ? Math.ceil(
                                              (paginatedHistoryData.totalCount ??
                                                  0) / historyParams.pageSize
                                          )
                                        : undefined
                                }
                                current={historyParams.pageNumber}
                                onPageChange={(page: number) =>
                                    setHistoryParams((prevParams) => ({
                                        ...prevParams,
                                        pageNumber: page,
                                    }))
                                }
                                style={{ float: 'right' }}
                                className="mb-5"
                            />
                        )}
                </>
            )}
        </Panel>
    );
};

export default History;
